export const primaryPendingKey = ownerId => `fos-primary-choice-v1:${ownerId}`
const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i
const fields = 'user_id,worn_on,wear_log_id,revision'
export const PRIMARY_SELECT = `${fields},wear:wear_logs!daily_primary_wears_owner_wear_fkey(*,applications:wear_applications!wear_applications_owner_wear_fkey(*))`
export function readPrimaryDraft(storage, ownerId) {
  if (!ownerId) return null
  const text = storage.getItem(primaryPendingKey(ownerId))
  if (!text) return null
  let value
  try { value = JSON.parse(text) } catch { throw Error('The saved primary choice cannot be read. No choice was sent.') }
  if (!value || value.version !== 1 || value.ownerId !== ownerId || !UUID.test(value.wearId) || !UUID.test(value.revision) || (value.expectedRevision !== null && !UUID.test(value.expectedRevision)) || !/^\d{4}-\d{2}-\d{2}$/.test(value.day)) throw Error('The saved primary choice is invalid. No choice was sent.')
  return value
}
export function storePrimaryDraft(storage, request) {
  if (readPrimaryDraft(storage, request.ownerId)) throw Error('Resolve the earlier primary choice before making another.')
  const text = JSON.stringify(request)
  storage.setItem(primaryPendingKey(request.ownerId), text)
  if (storage.getItem(primaryPendingKey(request.ownerId)) !== text) throw Error('Could not store this choice safely. Nothing was sent.')
  return readPrimaryDraft(storage, request.ownerId)
}
export function clearPrimaryDraft(storage, request) {
  if (readPrimaryDraft(storage, request.ownerId)?.revision !== request.revision) throw Error('The saved choice changed. Refresh before continuing.')
  storage.removeItem(primaryPendingKey(request.ownerId))
  if (storage.getItem(primaryPendingKey(request.ownerId)) !== null) throw Error('Could not clear the saved choice. Retry safely.')
}
export function validatedPrimary(row, ownerId, day) {
  if (!row) return { pointer:null, wear:null, stale:false }
  if (row.user_id !== ownerId || row.worn_on !== day || !UUID.test(row.revision)) throw Error('Primary selection confirmation is invalid.')
  const wear = row.wear
  const valid = wear?.id === row.wear_log_id && wear.user_id === ownerId && wear.worn_on === day
  if (valid && (wear.applications || []).some(app => app.user_id !== ownerId || app.wear_log_id !== wear.id)) throw Error('Primary application confirmation is invalid.')
  return { pointer:{user_id:row.user_id,worn_on:row.worn_on,wear_log_id:row.wear_log_id,revision:row.revision}, wear:valid ? wear : null, stale:!valid }
}
export async function readPrimary(client, ownerId, day) {
  const {data,error} = await client.from('daily_primary_wears').select(PRIMARY_SELECT).eq('user_id',ownerId).eq('worn_on',day).maybeSingle()
  if (error) throw error
  return validatedPrimary(data, ownerId, day)
}
export async function readDayWears(client, ownerId, day) {
  const rows=[]
  for(let offset=0;;offset+=200) {
    const {data,error}=await client.from('wear_logs').select('*,applications:wear_applications!wear_applications_owner_wear_fkey(*)').eq('user_id',ownerId).eq('worn_on',day).order('created_at',{ascending:false}).order('id',{ascending:false}).range(offset,offset+199)
    if(error)throw error
    if(!Array.isArray(data) || data.some(row=>row.user_id!==ownerId || row.worn_on!==day))throw Error('Today’s wear history could not be verified.')
    rows.push(...data)
    if(data.length<200)return rows
  }
}
export async function sendPrimaryDraft(client, request) {
  const session=await client.auth.getUser()
  if(session.error || session.data?.user?.id!==request.ownerId) throw Error('The account changed. This choice remains saved for its original account.')
  const current=await readPrimary(client,request.ownerId,request.day)
  const same=row=>row?.revision===request.revision && row.wear_log_id===request.wearId
  if(current.pointer?.revision===request.revision && !same(current.pointer))throw Error('This request identity names a different wear. Review the saved choice.')
  if(same(current.pointer))return {status:current.stale?'stale':'confirmed',selection:current}
  let result
  if(request.expectedRevision===null) result=await client.from('daily_primary_wears').upsert({user_id:request.ownerId,worn_on:request.day,wear_log_id:request.wearId,revision:request.revision},{onConflict:'user_id,worn_on',ignoreDuplicates:true}).select(fields)
  else result=await client.from('daily_primary_wears').update({wear_log_id:request.wearId,revision:request.revision}).eq('user_id',request.ownerId).eq('worn_on',request.day).eq('revision',request.expectedRevision).select(fields)
  if(result.error)throw result.error
  // Read back the active owner/day join after any response, even zero-row conflicts.
  const after=await readPrimary(client,request.ownerId,request.day)
  return same(after.pointer) ? {status:after.stale?'stale':'confirmed',selection:after} : {status:'conflict',selection:after}
}
export function primaryHero(wear, fragrances) {
  if(!wear)return null
  const group=fragrances.find(row=>row.id===wear.fragrance_id || row.sizes?.some(size=>size.id===wear.fragrance_id))
  return {log:wear,fragrance:group?{...group,id:wear.fragrance_id}:null,decision:group?'render':'blank',reason:'explicit_primary',blockedSecondSource:null}
}

export function currentPrimaryWear(selection, logs, ownerId, day) {
  const wear=selection?.wear
  if(!wear || wear.user_id!==ownerId || wear.worn_on!==day)return null
  const current=logs.find(row=>row.id===wear.id && row.user_id===ownerId)
  // Local edits/check-ins can arrive before the follow-up server read. Never
  // replace newer current-day fields with an older primary read snapshot.
  return current ? (current.worn_on===day ? current : null) : wear
}

export function inspectPrimaryDraft(storage, ownerId) {
  if (!ownerId) return {pending:null,corrupt:null}
  const raw=storage.getItem(primaryPendingKey(ownerId))
  try { return {pending:readPrimaryDraft(storage,ownerId),corrupt:null} }
  catch(error) { return {pending:null,corrupt:{raw,message:error.message}} }
}
// Caller holds the same owner Web Lock used by all primary-choice mutations.
export function quarantinePrimaryDraft(storage, ownerId, expectedRaw, quarantineId) {
  const key=primaryPendingKey(ownerId)
  if(storage.getItem(key)!==expectedRaw)throw Error('The saved draft changed. Refresh before recovery.')
  if(!inspectPrimaryDraft(storage,ownerId).corrupt)throw Error('This is a valid original choice. Use its normal retry or dismiss controls.')
  const backupKey=`${key}:quarantine:${quarantineId}`
  if(storage.getItem(backupKey)!==null)throw Error('Recovery backup already exists. Refresh before recovery.')
  storage.setItem(backupKey,expectedRaw)
  if(storage.getItem(backupKey)!==expectedRaw)throw Error('Could not preserve the unreadable draft. Nothing was cleared.')
  if(storage.getItem(key)!==expectedRaw)throw Error('The saved draft changed. Its recovery copy is preserved; nothing was cleared.')
  storage.removeItem(key)
  if(storage.getItem(key)!==null)throw Error('Could not clear the unreadable draft. Its recovery copy is preserved.')
  return backupKey
}
